import React from 'react';
import { Card, Select, Input, Alert, Typography, Space } from 'antd';
import { FormikProps } from 'formik';

const { TextArea } = Input;
const { Text } = Typography;

interface ApprovalAction {
    value: string;
    label: string;
}

interface WorkflowData {
    approvalActions: ApprovalAction[];
    valueData: Record<string, any>;
    changesData: Record<string, any>;
    beforeData: Record<string, any>;
    [key: string]: any;
}

interface FieldConfig {
    key?: string;
    props?: {
        fromContext?: Record<string, any>;
    };
}

interface ApprovalDecisionProps {
    formikProps: FormikProps<WorkflowData>;
    field: FieldConfig;
}

const ApprovalDecision: React.FC<ApprovalDecisionProps> = ({ formikProps, field }) => {
    const { values, errors, touched, setFieldValue } = formikProps;

    // Get approval actions from the workflow data
    const approvalActions = values?.approvalActions || [];

    // Get nested form values using the field key as base
    const baseKey = 'approval';
    const commentKey = `${baseKey}Comment`;
    const actionKey = `${baseKey}Action`;

    const handleActionChange = (value: string): void => {
        setFieldValue(actionKey, value);
    };

    const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
        setFieldValue(commentKey, e.target.value);
    };

    // Get current values
    const currentAction = values[actionKey] as string;
    const currentComment = values[commentKey] as string;

    // Get validation states
    const hasActionError = touched[actionKey] && errors[actionKey];
    const hasCommentError = touched[commentKey] && errors[commentKey];

    // Find current action label
    const currentActionLabel = approvalActions.find(
        (action) => action.value === currentAction
    )?.label || '';

    const getAlertType = (label: string): 'error' | 'warning' | 'info' => {
        if (label.toLowerCase().includes('reject')) return 'error';
        if (label.toLowerCase().includes('return')) return 'warning';
        return 'info';
    };

    return (
        <Card className="w-full">
            <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                    <Text strong>Select Action</Text>
                    <Select
                        value={currentAction}
                        onChange={handleActionChange}
                        placeholder="Choose an action"
                        style={{ width: '100%', marginTop: 8 }}
                        options={approvalActions}
                        disabled={approvalActions.length === 0}
                    />
                    {hasActionError && (
                        <Alert
                            // @ts-ignore
                            message={errors[actionKey]}
                            type="error"
                            showIcon
                            style={{ marginTop: 8 }}
                        />
                    )}
                    {approvalActions.length === 0 && (
                        <Alert
                            message="No approval actions available for this step"
                            type="warning"
                            showIcon
                            style={{ marginTop: 8 }}
                        />
                    )}
                </div>

                <div>
                    <Text strong>Comments</Text>
                    <TextArea
                        value={currentComment}
                        onChange={handleCommentChange}
                        placeholder="Enter your comments here..."
                        rows={4}
                        style={{ marginTop: 8 }}
                    />
                    {hasCommentError && (
                        <Alert
                            // @ts-ignore
                            message={errors[commentKey]}
                            type="error"
                            showIcon
                            style={{ marginTop: 8 }}
                        />
                    )}
                </div>

                {currentAction && (
                    <Alert
                        message={
                            <Space direction="vertical">
                                <Text>
                                    You have selected to{' '}
                                    <Text strong>{currentActionLabel}</Text>
                                    {' '}this request.
                                </Text>
                                <Text>
                                    Please ensure your comments are clear and complete.
                                </Text>
                            </Space>
                        }
                        type={getAlertType(currentActionLabel)}
                        showIcon
                    />
                )}
            </Space>
        </Card>
    );
};

export default ApprovalDecision;